
import { Box, IconButton, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import snapshotCapture from "../../services/snapshotCapture";
import React from "react";

import {
  fullName,
  getErrorString,
  getUserDetail,
  getUserId
} from "../../constants/function";
import { SocketContext } from "../../context/socket";
import { DEFAULT_MUSIC_TIMING, musicTimingFrom } from "../../components/Stream/frameClock";

import {
  setStreamCount,
} from "../../action/streamCount";

import PushPinIcon from "@mui/icons-material/PushPin";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import AgoraRTC from "agora-rtc-sdk-ng";
import { leaveStream } from "../../action/stream";
import { getBalance } from "../../action/balance";
import NftList from "../../components/DirectChat/nftList";
import Loader from "../../components/Loader";
import notifier from "../../components/Notifier";
import ActiveListings from "../../components/Stream/ActiveListings";
import StreamHeader from "../../components/Stream/AudiencCardContainer/StreamHeader";
import StreamTippers from "../../components/Stream/AudiencCardContainer/StreamTippers";
import StreamingChat from "../../components/Stream/StreamingChat";
import StreamingControls from "../../components/Stream/StreamingControls";
import SuccessPopup from "../../components/SuccessPopup";
import TipPopup from "../../components/TipPopup";
import TopListPopup from "../../components/TopListPopup";
import emitter from "../../EventEmitter";
import ApiService from "../../services/api-service";
import cookieStorage from "../../services/cookie-storage";
import { SERVER_URL } from "../../constants/api";
import { useAgoraRTC } from "../../services/useAgoraRTC";
import StreamingService from "./StreamingService";
import LivestreamMusicPlayer from "../../components/Stream/LivestreamMusicPlayer";
import HLSPlayer from "../../components/Stream/HLSPlayer";
import StreamShow from "../../components/Stream/StreamShow";
import TipAnimationStack from "../../components/TipAnimation/TipAnimationStack";

// Tips beyond this many on screen at once drop the oldest.
const MAX_VISIBLE_TIPS = 3;
// The in-stream DAW is hidden for now: no button, no panel, and session
// events from the server are ignored so viewers cannot be switched into it.
const DAW_IN_LIVE_ENABLED = false;
import DAW from "../DAW";
import PianoIcon from "@mui/icons-material/Piano";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LiveTopSupporters from "../../components/Stream/LiveTopSupporters";
// The page styles used to reach the bundle only through the deleted legacy
// pages/Streaming/index.js; this page owns them now.
import "./index.scss";
import "./live-layout.scss";

const REJOIN_HOST_COOLDOWN_MS = 5000;
const RECONNECTING_STATE = "RECONNECTING";


function Streaming({
  streamData: propStreamData,
  playlistId: propPlaylistId,
  title,
  goal,
  goalDescription,
  isJoined: propIsJoined,
  setIsJoined: propSetIsJoined,
  tokenData: propTokenData,
  setTokenData: propSetTokenData,
  obs = false,
  match,
  history,
  location,
}) {

  const { t } = useTranslation("common");
  const userId = getUserId();

  const playlistId = propPlaylistId || match?.params?.playlistId;

  const [streamData, setStreamData] = useState(propStreamData || null);
  const [isJoined, setIsJoined] = useState(propIsJoined || false);
  const [loading, setLoading] = useState(false);
  const setLoadingWithDebug = (value) => {
    setLoading(value);
  };

  const isOwner = streamData && userId ? (
    (streamData.owner && streamData.owner.toString() === userId.toString()) ||
    (streamData.createdBy && streamData.createdBy.toString() === userId.toString()) ||
    (streamData.createdByDetails?._id && streamData.createdByDetails._id.toString() === userId.toString())
  ) : false;

  const urlParams = new URLSearchParams(window.location.search);
  const urlRole = urlParams.get('role');
  const urlEnableVideo = urlParams.get('enableVideo') === 'true';

  const initialRole = urlRole || (isOwner ? "host" : "audience");

  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Extract token data from propStreamData if it was passed from GoLive_List
  // (which already called startLiveStream and got the token).
  // This avoids duplicate API calls to fetch the same token.
  const initialTokenData = (() => {
    if (propTokenData) return null; // propTokenData takes priority via effectiveTokenData
    if (propStreamData?.token && propStreamData?.appId && propStreamData?.channelName) {
      console.log("[Streaming] Token extracted from propStreamData — skipping duplicate API calls");
      return {
        token: propStreamData.token,
        appId: propStreamData.appId,
        channelName: propStreamData.channelName,
        uid: propStreamData.uid,
      };
    }
    return null;
  })();
  const [localTokenData, setLocalTokenData] = useState(initialTokenData);
  const effectiveTokenData = propTokenData || localTokenData;
  const effectiveSetTokenData = propSetTokenData || setLocalTokenData;

  // HLS vs RTC viewing mode for audience
  const [viewMode, setViewMode] = useState(null); // "hls" | "rtc" | null
  const [hlsUrl, setHlsUrl] = useState(null);
  // Direct OBS streams carry no playlist music; the viewer's player plays it.
  const [playsHostMusic, setPlaysHostMusic] = useState(false);
  const [hlsMuted, setHlsMuted] = useState(true);
  const [hlsDelaySec, setHlsDelaySec] = useState(null);
  // A direct OBS stream's stamped playlist and the wall-clock time of the
  // frame on screen it yields; the host's music follows that clock.
  const [hlsClockUrl, setHlsClockUrl] = useState(null);
  const [hostMusicTimeline, setHostMusicTimeline] = useState(null);
  // How the music follows the picture; from the join, updated live.
  const [musicTiming, setMusicTiming] = useState(DEFAULT_MUSIC_TIMING);
  const frameClockRef = useRef({ ms: null });
  // Samples arrive every 2 s; only a real change re-renders the page.
  const updateHlsDelay = useCallback((delaySec) => {
    setHlsDelaySec((current) => (current !== null && Math.abs(current - delaySec) < 0.5 ? current : delaySec));
  }, []);
  const hlsFallbackRef = useRef(null); // RTC credentials for HLS→RTC fallback

  // OBS admin view: host watching their own OBS stream via HLS with full controls
  const [isObsAdmin, setIsObsAdmin] = useState(false);

  const dispatch = useDispatch();

  const [tokenRestored, setTokenRestored] = useState(false);

  const isMountedRef = useRef(true);
  const hasEmittedRejoinHostRef = useRef(false);
  const hasReconnectedAsHostRef = useRef(false);
  const lastEmitTimeRef = useRef(0);
  const snapshotTimeoutsRef = useRef([]);

  const socketRef = useRef(null);
  const streamDataRef = useRef(null);
  const playlistIdRef = useRef(null);
  const userIdRef = useRef(null);
  const isJoinedRef = useRef(false);
  const leaveChannelRef = useRef(null);
  const forceLeaveChannelRef = useRef(null);
  const isEndingRef = useRef(false);
  const roleRef = useRef("audience");
  // Payloads of the last join emits, re-sent when the socket reconnects without session recovery
  const joinLivestreamPayloadRef = useRef(null);
  const rejoinHostPayloadRef = useRef(null);

  const emitRejoinHost = useCallback(() => {
    const currentSocket = socketRef.current;
    const payload = rejoinHostPayloadRef.current;
    if (!currentSocket || !payload || hasReconnectedAsHostRef.current) return;
    hasReconnectedAsHostRef.current = true;
    currentSocket.emit("rejoin-host", payload);
    setTimeout(() => {
      hasReconnectedAsHostRef.current = false;
    }, REJOIN_HOST_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    const container = document.getElementById("streaming-main-container");
    if (container) {
      container.style.display = "flex";
    }

    return () => {
      isMountedRef.current = false;
      hasEmittedRejoinHostRef.current = false;
      hasReconnectedAsHostRef.current = false;

      const currentSocket = socketRef.current;
      const currentStreamData = streamDataRef.current;
      const currentPlaylistId = playlistIdRef.current;
      const currentUserId = userIdRef.current;
      const currentIsJoined = isJoinedRef.current;

      try {
        forceLeaveChannelRef.current?.();
      } catch (error) {
        console.error("[Streaming] Error leaving Agora channel on unmount:", error);
      }

      if (currentSocket && currentSocket.connected && currentIsJoined) {
        console.log("[Streaming] Component unmounting - notifying backend user is leaving");
        currentSocket.emit("stream-end", {
          JWT: cookieStorage.getCookie("Authorization"),
          channelName: currentStreamData?._id || currentPlaylistId,
          role: roleRef.current
        });
      }
    };
  }, []);

  useEffect(() => {
    const fetchStreamData = async () => {
      if (streamData || !playlistId) return;

      try {
        setLoading(true);
        const response = await ApiService.PLAYLIST_DETAIL({
          _id: playlistId,
          pageNo: 1,
          pageSize: 1,
        });

        if (response?.result) {
          setStreamData(response.result);

          if (response.result.token && response.result.channelName && !tokenRestored && isMountedRef.current) {
            effectiveSetTokenData({
              token: response.result.token,
              appId: response.result.appId,
              channelName: response.result.channelName,
              uid: response.result.uid
            });
            setTokenRestored(true);
          }
        }
      } catch (error) {
        console.error('[Streaming] Error fetching stream data:', error);
        notifier.error('Failed to load stream data');
      } finally {
        setLoading(false);
      }
    };

    fetchStreamData();
  }, [playlistId, streamData]);

  const { Socket: socket } = useContext(SocketContext);
  const [isCohost, setIsCohost] = useState(false);
  // A moderator runs the jukebox and can kick, as the host can; nothing else.
  const [isModerator, setIsModerator] = useState(false);
  const [moderatorIds, setModeratorIds] = useState([]);
  const canModerate = isOwner || isModerator;

  useEffect(() => {
    if (!isJoined || !playlistId || !userId) return;
    let alive = true;
    ApiService.LIVESTREAM_MODERATORS_STATUS(playlistId)
      .then((res) => { if (alive) setIsModerator(!!res?.result?.isModerator); })
      .catch(() => {});
    if (isOwner) {
      ApiService.LIVESTREAM_MODERATORS_LIST()
        .then((res) => { if (alive) setModeratorIds((res?.result?.moderators || []).map((m) => String(m._id))); })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [isJoined, playlistId, userId, isOwner]);

  useEffect(() => {
    if (!socket) return;
    const handleModeratorChanged = (data) => {
      if (!data) return;
      if (data.channelName && data.channelName !== playlistId) return;
      if (String(data.userId) === String(userId)) {
        setIsModerator(!!data.isModerator);
        if (data.isModerator) notifier.success("You're now a moderator of this stream");
      }
      if (isOwner) {
        setModeratorIds((ids) => data.isModerator
          ? Array.from(new Set([...ids, String(data.userId)]))
          : ids.filter((id) => id !== String(data.userId)));
      }
    };
    socket.on('moderator-changed', handleModeratorChanged);
    return () => { socket.off('moderator-changed', handleModeratorChanged); };
  }, [socket, playlistId, userId, isOwner]);
  const [pinnedUid, setPinnedUid] = useState(null);
  const pinnedIsLocal = pinnedUid === "local";

  const [message, setMessage] = useState(null);
  const [success, setSuccess] = useState(false);

  const [liveUser, setLiveUser] = useState([]);
  const [tippers, setTippers] = useState(false);
  const [showNft, setShowNft] = useState(false);
  const [openTip, setOpenTip] = useState(false);
  const [userData, setUserData] = useState({});
  const [tipMetadata, setTipMetadata] = useState(null); // For livestream song tips (songId, hostId, livestreamId)
  const [openUser, setOpenUser] = useState(false);
  const [tipAnimations, setTipAnimations] = useState([]); // Queue of tip animations to show
  const seenTipIdsRef = useRef(new Set());
  const removeTipAnimation = useCallback((id) => {
    setTipAnimations(prev => prev.filter(t => t.id !== id));
  }, []);

  const handleTipHost = () => {
    if (streamData?.createdByDetails) {
      emitter.emit("openTipModal", { user: streamData.createdByDetails });
    } else {
      notifier.error("Unable to tip host - host information not available");
    }
  };

  const handleFollowHost = async (currentlyFollowing) => {
    if (!streamData?.createdByDetails?._id) {
      notifier.error("Unable to follow host - host information not available");
      return;
    }

    if (userId && streamData.createdByDetails._id?.toString() === userId.toString()) {
      notifier.info("You cannot follow your own stream");
      return;
    }

    try {
      if (currentlyFollowing) {
        await ApiService.UNFOLLOW({ following: streamData.createdByDetails._id, status: false });
        notifier.success("Unfollowed the host");
      } else {
        await ApiService.FOLLOW({ following: streamData.createdByDetails._id, status: true });
        notifier.success("Successfully followed the host!");
      }
      return !currentlyFollowing; // return new state
    } catch (error) {
      notifier.error(currentlyFollowing ? "Failed to unfollow host" : "Failed to follow host");
      return currentlyFollowing; // return unchanged state on error
    }
  };

  const handleSubscribeHost = async () => {
    if (!streamData?.createdByDetails?._id) {
      notifier.error("Unable to subscribe to host - host information not available");
      throw new Error("No host info");
    }

    try {
      setLoading(true);
      await ApiService.SUBSCRIBE_ARTIST({
        artistId: streamData.createdByDetails._id,
        currency: "siki",
      });

      notifier.success("Successfully subscribed to the host!");
    } catch (error) {
      notifier.error("Failed to subscribe to host - insufficient balance?");
      throw error;
    } finally {
      setLoading(false);
    }
  };


  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const lastPlayedVideoRef = useRef({ track: null, element: null }); // Guard against repeated .play() calls
  const playedCohostTilesRef = useRef(new WeakMap()); // tile element -> video track already playing in it
  const localVideoRef = useRef(null); // Direct ref to the <video> element for manual rendering
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showActiveListings, setShowActiveListings] = useState(false);

  const [isMute, setIsMute] = useState(false);
  const [video, setVideo] = useState(true);
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [apiCallInProgress, setApiCallInProgress] = useState(false);

  // In-stream video playback (host plays a video from playlist; audience watches side-by-side)
  const [activeStreamVideo, setActiveStreamVideo] = useState(null);

  // DAW collaborative session
  const [dawSessionActive, setDawSessionActive] = useState(false);
  const [songData, setSongData] = useState(null);
  const songTimelineRef = useRef([]); // [{_id, title, wallTime}] — tracks song changes for clip attribution

  const [enableAudio] = useState(true);
  const enableVideo = urlEnableVideo || isOwner;

  console.log("[Streaming] render — enableVideo:", enableVideo, "isOwner:", isOwner,
    "isObsAdmin:", isObsAdmin, "token?:", !!effectiveTokenData?.token,
    "streamData.owner:", streamData?.owner, "userId:", userId);

  const [lastSecond, setLastSecond] = useState(0);

  const videoContainerRef = useRef(null);
  const agoraHookResult = useAgoraRTC({
    appId: effectiveTokenData?.appId,
    channel: effectiveTokenData?.channelName,
    token: effectiveTokenData?.token,
    uid: effectiveTokenData?.uid ?? null,
    role: initialRole,
    enableAudio: enableAudio && !isObsAdmin,
    enableVideo: enableVideo && !isObsAdmin,
    onJoinSuccess: (assignedUid) => {
      console.log('[useAgoraRTC] Join success callback, clearing loading state');
      if (isMountedRef.current) {
        setLoading(false);
        setHasError(false);
        setErrorMessage('');
      }
    },
    isObs: obs,
  });

  const {
    localAudioTrack,
    localVideoTrack,
    localScreenTrack,
    isScreenSharing,
    setLocalAudioTrack,
    setLocalVideoTrack,
    musicTrack,
    publishMusicTrack,
    stopMusicTrack,
    publishDAWAudioTrack,
    stopDAWAudioTrack,
    dawAudioTrack,
    remoteUsers,
    joinChannel,
    leaveChannel,
    forceLeaveChannel,
    muteAudio,
    unmuteAudio,
    enableLocalVideo,
    disableLocalVideo,
    setClientRole,
    startScreenShare,
    stopScreenShare,
    connectionState,
    client,
  } = agoraHookResult;


  const { streamCount } = useSelector((state) => state.streamCount);
  // Ref mirror so the socket-handler closure can read the latest
  // value for the monotonic clamp without re-subscribing on every
  // render.
  const streamCountRef = useRef(streamCount);
  useEffect(() => { streamCountRef.current = streamCount; }, [streamCount]);
  const seek = useSelector((state) => state.seek);

  const [callCount, setCallCount] = useState(0);



  useEffect(() => {
    if (!client) {
      return;
    }

    const handleUserLeft = (user) => {
      setLiveUser((prev) => prev.filter((u) => u?.user?.uid !== user.uid));
    };

    client.on("user-left", handleUserLeft);

    return () => {
      client.off("user-left", handleUserLeft);
    };
  }, [client]);

  useEffect(() => {
    if (!socket) {
      console.log("[Streaming] Socket not available");
      return;
    }

    const handleAudienceInStream = (data) => {
      const effectivePlaylistId = streamData?._id || playlistId;
      if (data.channelName !== effectivePlaylistId) return;
      // Trust the server's authoritative list, including when it goes to 0
      // (audience legitimately left). The previous `length > 0` gate caused
      // the local placeholder ("Viewer") seeded by the join effect below to
      // stick when the broadcast race delivered an empty array first, and
      // also prevented audience-leave broadcasts from clearing departed
      // users from the rendered list.
      if (Array.isArray(data.liveUsers)) {
        setLiveUser([...data.liveUsers]);
      }
    };

    socket.on("audience-in-stream", handleAudienceInStream);

    const handleStreamCount = (data) => {
      const effectivePlaylistId = streamData?._id || playlistId;
      // Only process song-play-count events, ignore viewer-count events.
      // Match against channelName OR playlistId OR roomId on the payload —
      // progressBroadcast publishes with both channelName and playlistId set
      // to roomId, but historic broadcasts may only set one. Strict equality
      // on a single field caused web audience to silently drop every tick
      // when the broadcast field happened not to match streamData._id.
      const incomingIds = [
        data?.channelName?.toString?.() || data?.channelName,
        data?.playlistId?.toString?.() || data?.playlistId,
        data?.roomId?.toString?.() || data?.roomId,
      ].filter(Boolean);
      const myId = effectivePlaylistId?.toString?.() || effectivePlaylistId;
      if (
        myId && incomingIds.includes(myId) &&
        data.type === "song-play-count"
      ) {
        const incoming = Number(data.streamCount);
        if (!Number.isFinite(incoming)) return;
        // Monotonic clamp — stream count for a session must only go up.
        // Without this, an occasional regression broadcast (e.g. a
        // server-side blip between songs) would visibly reset the
        // audience's display while the host's continued growth was
        // correct.
        const current = Number(streamCountRef.current) || 0;
        if (incoming < current) return;
        dispatch(setStreamCount(incoming));
      }
    };

    socket.on("livestream-streamcount", handleStreamCount);

    const handleStreamEnded = (data) => {
      const effectivePlaylistId = streamData?._id || playlistId;
      if (data.channelName === effectivePlaylistId) {
        if (!isOwner || (data.endedBy && data.endedBy !== userId)) {
          console.log("[Streaming] => Stream ended by host. Closing overlay...");
          isEndingRef.current = true;
          try { forceLeaveChannelRef.current?.(); } catch {}
          exitLivePage();
        } else {
          console.log("[Streaming] => Stream ended by current user, handling locally");
        }
      }
    };

    socket.on("stream-ended", handleStreamEnded);

    const handleDisconnect = () => {
      console.log("[Streaming] Socket disconnected");
    };

    const handleConnect = () => {
      console.log("[Streaming] Socket connected, recovered:", socket.recovered);
      const joinPayload = joinLivestreamPayloadRef.current;
      if (socket.recovered || !isJoinedRef.current || !joinPayload) return;
      socket.emit("join-livestream", { ...joinPayload, JWT: cookieStorage.getCookie("Authorization") });
      emitRejoinHost();
    };

    socket.on("disconnect", handleDisconnect);
    socket.on("connect", handleConnect);

    // Listen for tip messages to show animation.
    // Two "received" payloads exist per tip: the tipper's chat_message relay
    // (has roomId) and a server-side copy published without a roomId to every
    // socket. Only the room-scoped one is shown, so a tip animates once and
    // never in a different stream.
    const handleTipMessage = (msg) => {
      if (!msg || msg.type !== "tip" || !msg.amount) return;
      const effectivePlaylistId = (streamData?._id || playlistId || "").toString();
      if (!msg.roomId || msg.roomId.toString() !== effectivePlaylistId) return;
      const dedupeKey = msg._id ? msg._id.toString() : null;
      if (dedupeKey) {
        if (seenTipIdsRef.current.has(dedupeKey)) return;
        seenTipIdsRef.current.add(dedupeKey);
        if (seenTipIdsRef.current.size > 200) {
          seenTipIdsRef.current = new Set(Array.from(seenTipIdsRef.current).slice(-100));
        }
      }

      let username = "Someone";
      if (msg.user) {
        username = fullName(msg.user);
      } else if (msg.message) {
        // "John Doe has tipped 10 SIKI to..."
        const match = msg.message.match(/^(.+?)\s+has tipped/);
        if (match) username = match[1];
      }

      const currency = msg.message?.match(/(SIKI|HBAR|MATIC|NEAR)/i)?.[1] || "SIKI";

      setTipAnimations(prev => [...prev, {
        id: dedupeKey || `${Date.now()}-${Math.random()}`,
        username,
        amount: msg.amount,
        currency: currency.toUpperCase(),
        profileImg: msg.user?.profileImg?.filePath || null,
      }].slice(-MAX_VISIBLE_TIPS));
    };

    socket.on("received", handleTipMessage);

    // DAW session events (viewer side)
    const handleDawSessionStart = (data) => {
      if (!DAW_IN_LIVE_ENABLED) return;
      const effectivePlaylistId = streamData?._id || playlistId;
      if (data.channelName === effectivePlaylistId) {
        setDawSessionActive(true);
      }
    };
    const handleDawSessionEnd = (data) => {
      const effectivePlaylistId = streamData?._id || playlistId;
      if (data.channelName === effectivePlaylistId) {
        setDawSessionActive(false);
      }
    };
    socket.on("daw:session-start", handleDawSessionStart);
    socket.on("daw:session-end", handleDawSessionEnd);

    const handleSongStarted = (data) => {
      const effectivePlaylistId = streamData?._id || playlistId;
      if (data && data.roomId === effectivePlaylistId) {
        if (data.type === "start" || data.type === "next" || data.type === "previous") {
          setSongData({ ...data });
          if (data._id) {
            songTimelineRef.current = [
              ...songTimelineRef.current.slice(-19),
              { _id: data._id, title: data.title || '', wallTime: Date.now() },
            ];
          }
        } else if (data.type === "progress" && data._id) {
          // Progress events fire continuously — detect song changes by comparing ID
          setSongData(prev => {
            if (prev?._id !== data._id) {
              songTimelineRef.current = [
                ...songTimelineRef.current.slice(-19),
                { _id: data._id, title: data.title || '', wallTime: Date.now() },
              ];
              return { ...data };
            }
            return prev;
          });
        } else if (data.type === "stop" || data.type === "pause") {
          setSongData(null);
        }
      }
    };
    socket.on("song-started", handleSongStarted);

    // In-stream video playback — host plays a playlist video, audience watches
    // it side-by-side with the host camera. Match on playlistId OR channelName
    // OR studioId (same permissive pattern as audience-in-stream).
    const isForThisStream = (data) => {
      const effective = (streamData?._id || playlistId || "").toString();
      const incoming = [data?.channelName, data?.playlistId, data?.studioId]
        .filter(Boolean)
        .map((v) => v.toString());
      return effective ? incoming.includes(effective) : false;
    };
    const handleVideoStarted = (data) => {
      if (!isForThisStream(data)) return;
      console.log("[Streaming] livestream-video-started:", data);
      setActiveStreamVideo({
        videoId: data.videoId,
        title: data.title,
        videoUrl: data.videoUrl,
        thumbnailUrl: data.thumbnailUrl,
        duration: data.duration,
        startedAt: data.startedAt || Date.now(),
      });
    };
    const handleVideoEnded = (data) => {
      if (!isForThisStream(data)) return;
      console.log("[Streaming] livestream-video-ended:", data);
      setActiveStreamVideo(null);
    };
    socket.on("livestream-video-started", handleVideoStarted);
    socket.on("livestream-video-ended", handleVideoEnded);

    return () => {
      socket.off("audience-in-stream", handleAudienceInStream);
      socket.off("livestream-streamcount", handleStreamCount);
      socket.off("stream-ended", handleStreamEnded);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect", handleConnect);
      socket.off("received", handleTipMessage);
      socket.off("daw:session-start", handleDawSessionStart);
      socket.off("daw:session-end", handleDawSessionEnd);
      socket.off("song-started", handleSongStarted);
      socket.off("livestream-video-started", handleVideoStarted);
      socket.off("livestream-video-ended", handleVideoEnded);
    };
  }, [socket, isOwner, streamData, playlistId, dispatch, emitRejoinHost]);

  // OBS RTMP status events (host-only) — surface NCS callbacks from skcore so
  // the streamer sees real-time feedback on whether OBS is actually reaching
  // Agora ingress. Channels are published from agoraNcsController.
  useEffect(() => {
    if (!socket || !isOwner || !streamData?._id) return;
    const channelId = streamData._id;
    const isForThisStream = (data) => data?.channelName === channelId;

    const handleConnected = (data) => {
      if (!isForThisStream(data)) return;
      console.log("[Streaming][OBS] rtmp-connected", data);
      notifier.success("OBS connected — your stream is live");
    };
    const handleDisconnected = (data) => {
      if (!isForThisStream(data)) return;
      console.warn("[Streaming][OBS] rtmp-disconnected", data);
      const secs = Math.round((data.gracePeriodMs || 30000) / 1000);
      notifier.error(`OBS disconnected — waiting ${secs}s for reconnect`);
    };
    const handleReconnected = (data) => {
      if (!isForThisStream(data)) return;
      console.log("[Streaming][OBS] rtmp-reconnected", data);
      notifier.success("OBS reconnected");
    };
    const handleGraceExpired = (data) => {
      if (!isForThisStream(data)) return;
      console.error("[Streaming][OBS] rtmp-grace-expired", data);
      notifier.error("OBS did not reconnect — stream ending");
    };
    const handleError = (data) => {
      if (!isForThisStream(data)) return;
      console.error("[Streaming][OBS] rtmp-error", data);
      notifier.error(`OBS error: ${data.errorMessage || data.errorCode || "unknown"}`);
    };

    socket.on("obs:rtmp-connected", handleConnected);
    socket.on("obs:rtmp-disconnected", handleDisconnected);
    socket.on("obs:rtmp-reconnected", handleReconnected);
    socket.on("obs:rtmp-grace-expired", handleGraceExpired);
    socket.on("obs:rtmp-error", handleError);

    return () => {
      socket.off("obs:rtmp-connected", handleConnected);
      socket.off("obs:rtmp-disconnected", handleDisconnected);
      socket.off("obs:rtmp-reconnected", handleReconnected);
      socket.off("obs:rtmp-grace-expired", handleGraceExpired);
      socket.off("obs:rtmp-error", handleError);
    };
  }, [socket, isOwner, streamData?._id]);

  // No local placeholder seed for liveUser. Previously this effect filled
  // the list with a fake `{firstName: "Host"|"Viewer"}` entry whenever
  // isJoined flipped true and the list was empty, then relied on the real
  // audience-in-stream broadcast to overwrite it. That overwrite never
  // happened when the broadcast was an empty array (gated out by
  // handleAudienceInStream above) or when the broadcast arrived before this
  // effect ran (the effect would then seed AFTER and clobber the real
  // data). Result was hosts and audience members seeing themselves
  // labeled "Viewer" with a count of 1 instead of the real roster.
  // The server's broadcast is authoritative; render whatever it sends.


  useEffect(() => {
    socketRef.current = socket;
    streamDataRef.current = streamData;
    playlistIdRef.current = playlistId;
    userIdRef.current = userId;
    isJoinedRef.current = isJoined;
    leaveChannelRef.current = leaveChannel;
    forceLeaveChannelRef.current = forceLeaveChannel;
    roleRef.current = isObsAdmin ? "host" : initialRole;
  }, [socket, streamData, playlistId, userId, isJoined, leaveChannel, forceLeaveChannel, isObsAdmin, initialRole]);

  const performForcefulCleanup = React.useCallback(() => {
    if (forceLeaveChannelRef.current) {
      try {
        forceLeaveChannelRef.current();
      } catch (error) {
        console.error("Error calling forceLeaveChannel:", error);
      }
    }

    if (isJoinedRef.current) {
      try {
        const token = cookieStorage.getCookie("Authorization");
        const channelName = streamDataRef.current?._id || playlistIdRef.current;
        const role = roleRef.current;
        // A non-forceful host end keeps the server's rejoin grace, so a reload can resume the stream
        const [endpoint, body] = role === "host"
          ? [`${SERVER_URL}/livestream/end/host`, { channelName, forceful: false }]
          : [`${SERVER_URL}/livestream/force-disconnect-audience`, { channelName, role, reason: "manual-leave" }];

        // The endpoint requires a verified JWT header, which sendBeacon cannot
        // set; keepalive lets the request outlive the page like a beacon would.
        if (token) {
          fetch(endpoint, {
            method: "POST",
            keepalive: true,
            headers: {
              "Content-Type": "application/json",
              Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
            },
            body: JSON.stringify(body),
          }).catch(() => {});
        }

        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit("stream-end", {
            JWT: cookieStorage.getCookie("Authorization"),
            channelName,
            role,
            reason: "manual-leave"
          });
        }
      } catch (error) {
        console.error("Error in cleanup notification:", error);
      }
    }
  }, []);

  // Tab close / reload / navigation away: leave Agora and tell the server,
  // once, whichever of pagehide (preferred) or beforeunload fires first.
  useEffect(() => {
    let cleanedUp = false;
    const handlePageHide = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      isEndingRef.current = true;
      performForcefulCleanup();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
    };
  }, [performForcefulCleanup]);

  useEffect(() => {
    const handleForcefulLeave = () => {
      isEndingRef.current = true;
      performForcefulCleanup();
    };

    emitter.on("forcefulLeaveStream", handleForcefulLeave);
    return () => {
      emitter.removeListener("forcefulLeaveStream", handleForcefulLeave);
    };
  }, [performForcefulCleanup]);

  const historyFromHook = useHistory();

  // The live page is shown two ways: as AppContainer's overlay (driven by the
  // redux `isStream` flag) or directly by the /app/LiveVideoStreaming/:id
  // route. Clearing the flag closes the overlay; the route copy has to
  // navigate away or End Live leaves the ended stream on screen.
  const exitLivePage = () => {
    dispatch(leaveStream());
    if (match) historyFromHook.replace("/app/Home");
  };

  const leaveStreamSentRef = useRef(false);

  useEffect(() => {
    if (!historyFromHook) return;

    const unlisten = historyFromHook.listen((location, action) => {
      if (!location.pathname.includes('/LiveVideoStreaming') && isJoinedRef.current && !leaveStreamSentRef.current) {
        leaveStreamSentRef.current = true;

        // leaveChannel unpublishes and closes the local tracks itself
        localAudioTrackRef.current = null;
        localVideoTrackRef.current = null;
        leaveChannelRef.current?.().catch((error) => {
          console.error("[Streaming] Error leaving channel on route change:", error);
        });

        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit("stream-end", {
            JWT: cookieStorage.getCookie("Authorization"),
            channelName: streamDataRef.current?._id || playlistIdRef.current,
            role: roleRef.current
          });
        }
      }
    });

    return unlisten;
  }, [historyFromHook]);

  useEffect(() => {
    if (!client) {
      return;
    }



    const updateLiveUser = (newUsers) => {
      setLiveUser((prev) => {
        const updatedList = [...prev, ...newUsers].filter(
          (user, index, self) =>
            index === self.findIndex((u) => u?.user?.uid === user?.user?.uid)
        );

        return updatedList;
      });
    };



   }, [client]);






  useEffect(() => {
    if (!socket || !client) {
      return;
    }

    const handleCohostAssigned = async (data) => {
      console.log('[Cohost] handleCohostAssigned received:', data);

      if (data.userId !== userId) return;
      try {
        if (data.role === 'host' || data.role === 'cohost') {
          // If viewer was watching HLS, upgrade to RTC with provided credentials
          if (viewMode === "hls" && data.rtcToken && data.rtcUid && data.appId) {
            console.log('[Cohost] Upgrading from HLS to RTC for co-host');
            setViewMode("rtc");
            setHlsUrl(null);
            setHlsClockUrl(null);
            effectiveSetTokenData({
              token: data.rtcToken,
              appId: data.appId,
              channelName: data.channelName,
              uid: data.rtcUid,
            });
            setAlreadyJoined(false); // Allow joinChannel to run
          }
          await setClientRole("host");
          await enableLocalVideo();
          setIsCohost(true);
          setPinnedUid("local");
        } else if (data.role === 'audience') {
          // Demoted back to audience — switch back to HLS if available
          await setClientRole("audience");
          setIsCohost(false);
          if (pinnedUid === 'local') setPinnedUid(null);

          // Re-enable HLS if Media Push is active
          const channelName = data.channelName || streamData?._id;
          if (channelName) {
            const hlsBase = process.env.REACT_APP_HLS_BASE_URL || 'https://staging-media.siki.io/hls';
            setHlsUrl(`${hlsBase}/${channelName}.m3u8`);
            setViewMode("hls");
          }
        }
      } catch (error) {
        console.error('[Cohost] Error handling role change:', error);
      }
    };

    socket.on("cohost-assigned", handleCohostAssigned);
    socket.on("makeCoHost", handleCohostAssigned);

    return () => {
      socket.off("cohost-assigned", handleCohostAssigned);
      socket.off("makeCoHost", handleCohostAssigned);
    };
  }, [socket, userId, client, setClientRole, enableLocalVideo]);

  useEffect(() => {
    if (!socket || !client) return;
    const handleRoleChanged = async (data) => {
      if (!data || data.channelName !== (playlistId || streamData?._id)) return;
      if (data.userId !== userId) return;
      try {
        if (data.role === 'audience') {
          await setClientRole('audience');
          setIsCohost(false);
          if (pinnedUid === 'local') setPinnedUid(null);
        } else if (data.role === 'host' || data.role === 'cohost') {
          await setClientRole('host');
          await enableLocalVideo();
          setIsCohost(true);
          setPinnedUid('local');
        }
      } catch (e) {
        console.warn('[Streaming] cohost role change failed:', e);
      }
    };
    socket.on('cohost-role-changed', handleRoleChanged);
    return () => { socket.off('cohost-role-changed', handleRoleChanged); };
  }, [socket, client, userId, playlistId, streamData, pinnedUid, setClientRole, enableLocalVideo]);

  const [cohostInvite, setCohostInvite] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      if (!data || data.userId !== userId) return;
      const currentChannel = streamData?._id || playlistId;
      if (!currentChannel || data.channelName !== currentChannel) return;
      setCohostInvite(data);
    };
    socket.on("cohost-invite", handler);
    return () => {
      socket.off("cohost-invite", handler);
    };
  }, [socket, userId, playlistId, streamData]);

  useEffect(() => {
    setInviteBusy(false);
  }, [cohostInvite]);

  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      if (!data) return;
      if (data.userId !== userId) return;
      const currentChannel = streamData?._id || playlistId;
      if (!currentChannel || data.channelName !== currentChannel) return;
      if (cohostInvite && data.inviteId !== cohostInvite.inviteId) return;
      if (data.status === "expired" || data.status === "canceled") {
        setCohostInvite(null);
      }
    };
    socket.on("cohost-invite-response", handler);
    return () => { socket.off("cohost-invite-response", handler); };
  }, [socket, userId, playlistId, streamData, cohostInvite]);

  useEffect(() => {
    if (!cohostInvite) return;
    const now = Date.now();
    const exp = typeof cohostInvite.expiresAt === "number" ? cohostInvite.expiresAt : new Date(cohostInvite.expiresAt).getTime();
    const delay = Math.max(0, exp - now);
    if (!isFinite(delay) || delay === 0) {
      setCohostInvite(null);
      return;
    }
    const t = setTimeout(() => setCohostInvite(null), delay);
    return () => clearTimeout(t);
  }, [cohostInvite]);


// Stream count is calculated server-side (socketLivestreamProvider) and
// broadcast via "livestream-streamcount" events. The server accounts for
// HLS viewer latency so counts only reflect actual listening.
// The single handler for this event lives in the useEffect above (~line 400).


// Track ref + loading + pinnedUid — fires when localVideoTrack changes
useEffect(() => {
  if (localVideoTrack) {
    localVideoTrackRef.current = localVideoTrack;

    if (isMountedRef.current) {
      setLoading(false);
    }

    if (isOwner && pinnedUid !== "local") {
      setPinnedUid("local");
    }
  }
}, [localVideoTrack, isOwner]);

// Owner camera preview — attach Agora's raw MediaStreamTrack directly to a <video> element.
// This bypasses Agora's play() pipeline (which delays frames ~30s) and uses no manual DOM.
useEffect(() => {
  const vid = localVideoRef.current;
  if (!vid || !localVideoTrack) return;

  try {
    const rawTrack = localVideoTrack.getMediaStreamTrack();
    if (rawTrack && rawTrack.readyState === 'live') {
      vid.srcObject = new MediaStream([rawTrack]);
      console.log("[Streaming] Owner preview: srcObject set via getMediaStreamTrack()");
    } else {
      // Track exists but not live yet — fall back to Agora's internal stream
      console.log("[Streaming] Owner preview: rawTrack not live, trying Agora play()");
      localVideoTrack.play(vid.parentElement);
    }
  } catch (err) {
    console.error("[Streaming] Owner preview: getMediaStreamTrack() failed, using Agora play():", err);
    localVideoTrack.play(vid.parentElement);
  }
}, [localVideoTrack]);

// Snapshot capture — separate effect so streamData._id changes don't re-trigger play()
useEffect(() => {
  if (!localVideoTrack || !isOwner || isObsAdmin || !streamData?._id) return;

  let retryCount = 0;
  const MAX_RETRIES = 10;
  snapshotTimeoutsRef.current = [];

  const startSnapshotCapture = () => {
    const videoElements = document.querySelectorAll('video');
    let targetVideo = null;

    for (const video of videoElements) {
      if (video.srcObject && !video.paused && video.readyState >= 2) {
        targetVideo = video;
        break;
      }
    }

    if (targetVideo) {
      snapshotCapture.startCapturing(targetVideo, streamData._id);
    } else if (retryCount < MAX_RETRIES) {
      retryCount++;
      const t = setTimeout(startSnapshotCapture, 1000);
      snapshotTimeoutsRef.current.push(t);
    }
  };

  const initialTimeout = setTimeout(startSnapshotCapture, 2000);
  snapshotTimeoutsRef.current.push(initialTimeout);

  return () => {
    snapshotTimeoutsRef.current.forEach(t => clearTimeout(t));
    snapshotTimeoutsRef.current = [];
    snapshotCapture.stopCapturing();
  };
}, [localVideoTrack, isOwner, isObsAdmin, streamData?._id]);

useEffect(() => {
  if (localAudioTrack) {
    localAudioTrackRef.current = localAudioTrack;
  }
}, [localAudioTrack]);

const fetchAvailableDevices = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioDevices = devices.filter((device) => device.kind === "audioinput");
    const videoDevices = devices.filter((device) => device.kind === "videoinput");


    return { audioDevices, videoDevices };
  } catch (error) {
    return { audioDevices: [], videoDevices: [] };
  }
};

const switchAudio = async (micLabel) => {
  try {
    const { audioDevices } = await fetchAvailableDevices();
    const selectedDevice = audioDevices.find((device) => device.label === micLabel);

    if (!selectedDevice) {
      console.warn(`[switchAudio] Device not found: ${micLabel}`);
      return;
    }

    console.log(`[switchAudio] Switching to: ${micLabel}`);

    if (localAudioTrackRef.current) {
      await client.unpublish(localAudioTrackRef.current);
      localAudioTrackRef.current.close();
    }

    const newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
      microphoneId: selectedDevice.deviceId,
    });

    await client.publish(newAudioTrack);

    localAudioTrackRef.current = newAudioTrack;
    setLocalAudioTrack(newAudioTrack);

    console.log("[switchAudio] Successfully switched microphone");

  } catch (error) {
    console.error("[switchAudio] Error switching microphone:", error);
  }
};

useEffect(() => {
}, []);



async function switchVideo(cameraLabel) {
  try {
    const { videoDevices } = await fetchAvailableDevices();
    const selectedDevice = videoDevices.find((device) => device.label === cameraLabel);

    if (!selectedDevice) {
      return;
    }

    if (!client) {
      return;
    }

    if (localVideoTrackRef.current) {
      return;
    }

    const newVideoTrack = await AgoraRTC.createCameraVideoTrack({
      cameraId: selectedDevice.deviceId,
    });

    await client.publish(newVideoTrack);

    localVideoTrackRef.current = newVideoTrack;
    setLocalVideoTrack(newVideoTrack);

  } catch (error) {
  }
}



  useEffect(() => {
    if (isOwner && pinnedUid !== "local") {
      setPinnedUid("local");
    } else if (!isOwner) {
      if (!pinnedUid && remoteUsers.length) {
        setPinnedUid(remoteUsers[0].uid);
      } else if (pinnedUid === "local") {
        setPinnedUid(remoteUsers[0]?.uid || null);
      }
    }
  }, [isOwner, pinnedUid, remoteUsers]);

  useEffect(() => {
    const handleEndStream = () => {
      isEndingRef.current = true;
      if (isOwner) endStream();
      else setSuccess(true);
    };
    emitter.on("endStream", handleEndStream);

    return () => {
      emitter.removeListener("endStream", handleEndStream);
    };
  }, [isOwner]);

  useEffect(() => {
    const handleOpenTipModal = (data) => {
      if (data?.user) {
        setUserData(data.user);
        // Store metadata if this is a livestream song tip
        if (data.tipType === "livestreamSong") {
          setTipMetadata({
            songId: data.songId,
            hostId: data.hostId,
            livestreamId: data.livestreamId,
            tipType: data.tipType
          });
        } else {
          setTipMetadata(null);
        }
        setOpenTip(true);
      }
    };

    emitter.on("openTipModal", handleOpenTipModal);

    return () => {
      emitter.removeListener("openTipModal", handleOpenTipModal);
    };
  }, []);

function endStream() {
  if (isOwner) {
    snapshotCapture.stopCapturing();
  }

  if (socket && streamData?._id) {
    socket.emit("stream-ended", {
      channelName: streamData._id,
      endedBy: userId
    });
    socket.emit("leave-livestream", { channelName: streamData._id, userId });
  }

  // Fire-and-forget so a hung await (e.g. client.unpublish during in-flight
  // join) cannot block teardown and leave the user reconnected.
  if (streamData?._id) {
    StreamingService.endLiveStream(streamData._id).catch((err) =>
      console.error("[Streaming] => Error ending livestream:", err)
    );
    StreamingService.exitLiveStream({ channelName: streamData._id, userId }).catch((err) =>
      console.error("[Streaming] => Error exiting livestream:", err)
    );
  }

  try {
    if (localVideoTrackRef.current) {
      try { localVideoTrackRef.current.close(); } catch {}
      localVideoTrackRef.current = null;
    }
    if (localAudioTrackRef.current) {
      try { localAudioTrackRef.current.close(); } catch {}
      localAudioTrackRef.current = null;
    }
  } catch {}

  try { forceLeaveChannel?.(); } catch {}

  // Remove the UI synchronously — relying on a window.location redirect was
  // racy when API calls hung mid-join.
  exitLivePage();
}
 const startStreamAPI = async () => {
  if (!streamData?._id) {
    return;
  }

  if (streamData.token && streamData.channelName) {
    if (isMountedRef.current) {
      effectiveSetTokenData({
        token: streamData.token,
        appId: streamData.appId,
        channelName: streamData.channelName,
        uid: streamData.uid
      });
    }
    setLoading(false);
    return;
  }

  try {
    setLoading(true);
    const res = await StreamingService.startLiveStream({
      channelName: streamData._id,
      goalAmount: goal,
      goalTitle: title,
      goalDescription: goalDescription,
    });
    if (!res?.success) {
      if (res?.message?.includes("already live")) {
        try {
          const rejoinRes = await StreamingService.joinLiveStream({
            channelName: streamData._id,
            platform: "web",
          });
          if (rejoinRes?.success) {
            const { token, appId, channelName, uid, obsAdmin, useHls, hlsUrl: serverHlsUrl, songStream } = rejoinRes.data || {};
            if (songStream?._id) {
              setSongData(songStream);
              if (!songTimelineRef.current.some(s => s._id === songStream._id)) {
                songTimelineRef.current.push({ _id: songStream._id, title: songStream.title || '', wallTime: Date.now() - 5 * 60 * 1000 });
              }
            }
            if (obsAdmin && useHls && serverHlsUrl) {
              // OBS admin view — watch via HLS with host controls, RTC as fallback
              console.log("[Streaming] Joining as OBS admin view");
              if (isMountedRef.current) {
                setIsObsAdmin(true);
                setHlsUrl(serverHlsUrl);
                setViewMode("hls");
                setAlreadyJoined(true);
                setIsJoined(true);
                setLoading(false);
                if (propSetIsJoined) propSetIsJoined(true);
                // Store RTC credentials so HLS can fall back to Agora if needed
                if (token && appId && uid) {
                  hlsFallbackRef.current = { token, appId, channelName, uid };
                }
              }
              return;
            }
            if (isMountedRef.current) {
              effectiveSetTokenData({ token, appId, channelName, uid });
              setLoading(false);
            }
            return;
          }
        } catch (rejoinErr) {
          console.error("[Streaming] Join as host failed:", rejoinErr);
        }
      }
      if (res?.message) {
        notifier.error(res.message);
      } else {
        notifier.error("Failed to start livestream");
      }
      setLoading(false);
      return;
    }
    const { token, appId, channelName, uid } = res.data || {};
    if (!token) {
      console.error("[Streaming] No token in response!");
      setLoading(false);
      return;
    }
    if (isMountedRef.current) {
      effectiveSetTokenData({ token, appId, channelName, uid });
    }

    if (socket) {
      const jwt = cookieStorage.getCookie("Authorization");
      socket.emit("stream-start", {
        JWT: jwt,
        channelName: streamData._id,
        role: "host",
        goalAmount: goal,
        goalTitle: title,
        goalDescription: goalDescription,
        platform: "web",
        isNew: true,
        fromObs: obs,
      });
    }
  } catch (err) {
    console.error("[Streaming] => startStreamAPI => error =>", err);

    if (err?.response?.data?.message?.includes("already live")) {
      console.log("[Streaming] Stream already live (from catch), attempting to join as host");
      try {
        const rejoinRes = await StreamingService.joinLiveStream({
          channelName: streamData._id,
          platform: "web",
        });
        if (rejoinRes?.success) {
          const { token, appId, channelName, uid, obsAdmin, useHls, hlsUrl: serverHlsUrl, songStream } = rejoinRes.data || {};
          if (songStream?._id) {
              setSongData(songStream);
              if (!songTimelineRef.current.some(s => s._id === songStream._id)) {
                songTimelineRef.current.push({ _id: songStream._id, title: songStream.title || '', wallTime: Date.now() - 5 * 60 * 1000 });
              }
            }
          if (obsAdmin && useHls && serverHlsUrl) {
            console.log("[Streaming] Joining as OBS admin view (from catch)");
            if (isMountedRef.current) {
              setIsObsAdmin(true);
              setHlsUrl(serverHlsUrl);
              setViewMode("hls");
              setAlreadyJoined(true);
              setIsJoined(true);
              setLoading(false);
              if (propSetIsJoined) propSetIsJoined(true);
              if (token && appId && uid) {
                hlsFallbackRef.current = { token, appId, channelName, uid };
              }
            }
            return;
          }
          console.log("[Streaming] Join as host successful:", { token, appId, channelName, uid });
          if (isMountedRef.current) {
            effectiveSetTokenData({ token, appId, channelName, uid });
            setLoading(false);
          }
          return;
        }
      } catch (rejoinErr) {
        console.error("[Streaming] Join as host failed:", rejoinErr);
      }
    }

    setLoading(false);
  }
};

  const joinStreamAPI = async () => {
    if (!streamData?._id) {
      return;
    }
    try {
      setLoading(true);
      const res = await StreamingService.joinLiveStream({
        channelName: streamData._id,
        platform: "web",
      });
      if (!res?.success) {
        notifier.error(res?.message || "Failed to join livestream");
        setLoading(false);
        return;
      }
      const { token, appId, channelName, uid, useHls: serverUseHls, hlsUrl: serverHlsUrl, obsAdmin, songStream, liveUsers, playsHostMusic: serverPlaysHostMusic, hlsClockUrl: serverHlsClockUrl, hostMusicTimeline: serverHostMusicTimeline, streamConfig: serverStreamConfig } = res.data || {};
      if (isMountedRef.current) setPlaysHostMusic(serverPlaysHostMusic === true);

      // Seed the viewer list from the join response so avatars show on first
      // paint. The audience-in-stream broadcast that follows stays
      // authoritative and overwrites this.
      if (Array.isArray(liveUsers) && isMountedRef.current) {
        setLiveUser([...liveUsers]);
      }

      // Initialize current song from join response so ClipEditor shows it immediately
      if (songStream?._id) {
              setSongData(songStream);
              if (!songTimelineRef.current.some(s => s._id === songStream._id)) {
                songTimelineRef.current.push({ _id: songStream._id, title: songStream.title || '', wallTime: Date.now() - 5 * 60 * 1000 });
              }
            }

      // OBS admin view — host watching their own OBS stream
      if (obsAdmin && serverUseHls && serverHlsUrl) {
        console.log("[Streaming] Joining as OBS admin view (from joinStreamAPI)");
        if (isMountedRef.current) {
          setIsObsAdmin(true);
          setHlsUrl(serverHlsUrl);
          setViewMode("hls");
          setAlreadyJoined(true);
          setIsJoined(true);
          setLoading(false);
          if (propSetIsJoined) propSetIsJoined(true);
          if (token && appId && uid) {
            hlsFallbackRef.current = { token, appId, channelName, uid };
          }
        }
        return;
      }

      // Server returned HLS URL — viewer watches via HLS instead of Agora RTC
      // RTC token is always included as fallback if HLS isn't ready
      if (serverUseHls && serverHlsUrl) {
        if (isMountedRef.current) {
          setHlsUrl(serverHlsUrl);
          setHlsClockUrl(serverPlaysHostMusic && serverHlsClockUrl ? serverHlsClockUrl : null);
          setHostMusicTimeline(serverPlaysHostMusic ? serverHostMusicTimeline || null : null);
          setMusicTiming((current) => musicTimingFrom(serverStreamConfig, current));
          setViewMode("hls");
          setAlreadyJoined(true);
          setIsJoined(true);
          setLoading(false);
          if (propSetIsJoined) propSetIsJoined(true);
          // Store RTC credentials as fallback in case HLS fails
          if (token && appId && uid) {
            hlsFallbackRef.current = { token, appId, channelName, uid };
          }
        }
        return;
      }

      if (!token) {
        setLoading(false);
        return;
      }
      if (isMountedRef.current) {
        effectiveSetTokenData({ token, appId, channelName, uid });
      }
    } catch (err) {
      console.error("[Streaming] => joinStreamAPI => error =>", err);
      setLoading(false);
    }
  };

  function cleanupAudio() {
  }

 async function localCleanupAndExit(redirect = true) {
  if (socket && streamData?._id) {
    socket.emit("leave-livestream", { channelName: streamData._id, userId });
  }

  try {
    const response = await StreamingService.exitLiveStream({
      channelName: streamData._id,
      userId: userId,
    });

    if (!response.success) {
      console.error("[Streaming] => Failed to exit livestream:", response.message);
    }
  } catch (error) {
    console.error("[Streaming] => Error exiting livestream:", error);
  }

  try {
    if (localVideoTrackRef.current && client) {
      await client.unpublish(localVideoTrackRef.current);
      localVideoTrackRef.current.close();
      localVideoTrackRef.current = null;
    }
    if (localAudioTrackRef.current && client) {
      await client.unpublish(localAudioTrackRef.current);
      localAudioTrackRef.current.close();
      localAudioTrackRef.current = null;
    }
  } catch (cleanupError) {
    console.error("[Streaming] => Error cleaning up local tracks:", cleanupError);
  }

  cleanupAudio();
  leaveChannel();

  if (redirect) {
    window.location.replace(`${window.location.origin}/app/Home`);
  }
}

  useEffect(() => {
    if (!streamData || !userId) return;
    if (apiCallInProgress) return;
    if (isEndingRef.current) return;

    if (!effectiveTokenData?.token && !alreadyJoined) {
      const shouldBeHost = urlRole === 'host' || isOwner;
      setApiCallInProgress(true);
      const apiCall = shouldBeHost ? startStreamAPI() : joinStreamAPI();
      apiCall.finally(() => {
        setApiCallInProgress(false);
      });
    } else if (effectiveTokenData?.token && !alreadyJoined) {
      // Mark joined immediately to prevent duplicate calls while async joinChannel runs
      setAlreadyJoined(true);
      setIsJoined(true);
      if (propSetIsJoined) {
        propSetIsJoined(true);
      }

      joinChannel().then(() => {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }).catch((err) => {
        console.error("[Streaming] joinChannel failed:", err);
        if (isMountedRef.current) {
          setLoading(false);
        }
      });
    }
  }, [effectiveTokenData?.token, alreadyJoined, urlRole, isOwner, streamData, userId, apiCallInProgress]);


  // An operator re-timed viewers from the server (services/media/streamConfig.js).
  useEffect(() => {
    if (!socket) return undefined;
    const handleStreamConfig = (data) => setMusicTiming((current) => musicTimingFrom(data?.streamConfig, current));
    socket.on("stream-config-updated", handleStreamConfig);
    return () => socket.off("stream-config-updated", handleStreamConfig);
  }, [socket]);

  // Tells the server how far behind the host this viewer runs, so the host's
  // dock can show it next to the delay the app viewers were given.
  useEffect(() => {
    if (!playsHostMusic || hlsDelaySec === null || !socket || !streamData?._id) return;
    socket.emit("viewer-sync-report", {
      channelName: streamData._id,
      platform: "web",
      source: frameClockRef.current.ms === null ? "measured" : "clock",
      delayMs: Math.round(hlsDelaySec * 1000),
    });
  }, [playsHostMusic, hlsDelaySec, socket, streamData?._id]);

  useEffect(() => {
    if (alreadyJoined && socket && streamData?._id) {
      const joinPayload = {
        channelName: streamData._id,
        role: isObsAdmin ? "host" : initialRole,
        platform: 'web',
        userId: userId,
        enableVideo: !isObsAdmin,
      };
      joinLivestreamPayloadRef.current = joinPayload;
      socket.emit("join-livestream", { ...joinPayload, JWT: cookieStorage.getCookie("Authorization") });

      if (isMountedRef.current) {
        setLoading(false);
      }

      rejoinHostPayloadRef.current = isOwner && !isObsAdmin
        ? { channelName: streamData._id, userId, agoraUid: effectiveTokenData?.uid, role: "host" }
        : null;

      if (rejoinHostPayloadRef.current && tokenRestored && !hasEmittedRejoinHostRef.current) {
        hasEmittedRejoinHostRef.current = true;
        socket.emit("rejoin-host", rejoinHostPayloadRef.current);
      }
    }
  }, [alreadyJoined, socket, streamData?._id, userId, initialRole, isOwner, urlRole, effectiveTokenData?.uid]);


  // "reconnect" is a Manager event in socket.io v4, so it lives on socket.io
  useEffect(() => {
    if (!socket?.io) return;
    socket.io.on("reconnect", emitRejoinHost);
    return () => {
      socket.io.off("reconnect", emitRejoinHost);
    };
  }, [socket, emitRejoinHost]);

  const [isUnmounting, setIsUnmounting] = useState(false);

  useEffect(() => {
    if (streamData && isJoined) {
    }
  }, [streamData, isJoined]);

  const handleMuteToggle = async () => {
    if (isMute) {
      await unmuteAudio();
      setIsMute(false);
    } else {
      await muteAudio();
      setIsMute(true);
    }
  };

  const handleVideoToggle = async () => {
    if (video) {
      await disableLocalVideo();
      setVideo(false);
    } else {
      await enableLocalVideo();
      setVideo(true);
    }
  };

  const openFullScreen = async () => {
    const elem = document.getElementById("__streaming_container");
    if (elem && !isFullScreen) {
      setIsFullScreen(true);
      await elem.requestFullscreen();
    }
  };

  const closeFullScreen = async () => {
    if (document.exitFullscreen) {
      setIsFullScreen(false);
      await document.exitFullscreen();
    }
  };

  // Esc (or the browser's own exit) leaves fullscreen without going through
  // closeFullScreen, so follow the document instead of trusting local state.
  useEffect(() => {
    const syncFullScreen = () => setIsFullScreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", syncFullScreen);
    return () => document.removeEventListener("fullscreenchange", syncFullScreen);
  }, []);

  const [tipLoading, setTipLoading] = useState(false);
  const handleTip = async (amount, id, type, currency) => {
    if (!amount) {
      setOpenTip(false);
      setTipMetadata(null);
      return;
    }
    try {
      const payload = {
        id: id || userData._id,
        tipAmount: amount,
        type: tipMetadata?.tipType === "livestreamSong" ? "livestreamSong" : "livestreamArtist",
        currency: currency,
        // Add livestream song tip metadata
        ...(tipMetadata?.tipType === "livestreamSong" && {
          songId: tipMetadata.songId,
          hostId: tipMetadata.hostId,
          livestreamId: tipMetadata.livestreamId
        })
      };

      setTipLoading(true);
      setOpenTip(false);

      const response = await ApiService.TIP_PLAYLIST(payload);

      setTipLoading(false);
      setTipMetadata(null); // Clear metadata after successful tip
      // Don't show toast - host will see their own tip animation
      // notifier.success(response["result"]);
      dispatch(getBalance());

      // Broadcast tip message to chat
      const userDetail = getUserDetail();
      const recipientName = userData.firstName && userData.lastName
        ? fullName(userData)
        : (tipMetadata?.tipType === "livestreamSong" ? "the song" : "the stream");

      const msg = {
        isStreaming: true,
        roomId: streamData?._id || playlistId,
        senderUserId: userDetail._id,
        amount: amount,
        message: `${fullName(userDetail)} has tipped ${amount} ${currency.toUpperCase()} to ${recipientName}`,
        _id: userDetail._id,
        JWT: cookieStorage.getCookie("Authorization"),
        type: "tip",
        user: {
          firstName: userDetail["firstName"],
          lastName: userDetail["lastName"],
          profileImg: userDetail["profileImg"],
          _id: userDetail["_id"],
          isVerified: userDetail["isVerified"],
        },
      };

      if (socket) socket.emit("chat_message", msg);
    } catch (err) {
      setTipLoading(false);
      setTipMetadata(null);
      notifier.error(getErrorString(err));
    }
  };


  // Fall back from HLS to RTC if HLS stream isn't available
  const handleHlsFallback = () => {
    const fallback = hlsFallbackRef.current;
    if (!fallback) return;
    console.log('[Streaming] HLS failed, falling back to RTC');
    setViewMode("rtc");
    setHlsUrl(null);
    setHlsClockUrl(null);
    setAlreadyJoined(false); // Allow joinChannel to run via useEffect
    effectiveSetTokenData(fallback);
    hlsFallbackRef.current = null;
  };

  const renderPinnedVideoArea = () => {
    // HLS mode: audience watching via HLS, or OBS admin viewing own stream
    if (viewMode === "hls" && hlsUrl && ((!isOwner && !isCohost) || isObsAdmin)) {
      return (
        <Box
          className="pinned_area"
          sx={{
            position: "relative",
            flex: 1,
            backgroundColor: "var(--background-color, #000)",
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isObsAdmin && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                zIndex: 10,
                backgroundColor: "rgba(76, 175, 80, 0.9)",
                padding: "4px 12px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  animation: "pulse 2s infinite",
                }}
              />
              <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>
                Streaming via OBS
              </Typography>
            </Box>
          )}
          <HLSPlayer
            streamUrl={hlsUrl}
            clockUrl={hlsClockUrl}
            frameClockRef={frameClockRef}
            isPlaying={true}
            onMutedChange={setHlsMuted}
            onDelayChange={updateHlsDelay}
            onError={(err) => {
              console.error('[Streaming] HLS playback error:', err);
              // Fall back to RTC if HLS stream isn't available
              if (hlsFallbackRef.current) handleHlsFallback();
            }}
            style={{ width: "100%", height: "100%" }}
          />

          {/* Tip animations overlay */}
          <TipAnimationStack tips={tipAnimations} onComplete={removeTipAnimation} />
        </Box>
      );
    }

    if (isOwner) {

      // If using OBS, the stream appears as a remote video track via Agora Media Gateway
      // Show a message to the host that they're streaming from OBS
      if (obs) {
        return (
          <Box
            className="pinned_area"
            sx={{
              position: "relative",
              flex: 1,
              backgroundColor: "var(--background-color, #000)",
              minHeight: "100%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Typography variant="h4" sx={{ color: "#4caf50", fontWeight: "bold" }}>
              🎥 Streaming from OBS
            </Typography>
            <Typography variant="body1" sx={{ color: "#fff", opacity: 0.8 }}>
              Your stream is live! Monitor your stream in OBS.
            </Typography>
            <Typography variant="body2" sx={{ color: "#fff", opacity: 0.6 }}>
              Your audience can see your stream through Agora.
            </Typography>

            {/* Tip animations overlay */}
            <TipAnimationStack tips={tipAnimations} onComplete={removeTipAnimation} />
          </Box>
        );
      }

      return (
        <Box
          className="pinned_area"
          sx={{
            position: "relative",
            flex: 1,
            backgroundColor: "var(--background-color, #000)",
            minHeight: "100%",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Owner camera — React-controlled <video> element, srcObject set via useEffect */}
          <video
            ref={localVideoRef}
            id="owner-camera-preview"
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              objectFit: "contain",
              backgroundColor: "#000",
              transform: "scaleX(-1)",
            }}
          />
          {!localVideoTrack && (
            <Box sx={{ position: "absolute", zIndex: 1, textAlign: 'center', color: '#fff' }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Initializing Camera...
              </Typography>
              {!video && (
                <Typography variant="body2" sx={{ mt: 2, color: '#ff9800' }}>
                  Video is currently disabled. Click the camera button to enable.
                </Typography>
              )}
            </Box>
          )}

          {/* Tip animations overlay */}
          <TipAnimationStack tips={tipAnimations} onComplete={removeTipAnimation} />
        </Box>
      );
    }

    if (pinnedIsLocal) {
      return (
        <Box
          className="pinned_area"
          sx={{
            position: "relative",
            flex: 1,
            backgroundColor: "var(--background-color, #000)",
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {localVideoTrack ? (
            <div
              id="local-video-container"
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--background-color, #000)"
              }}
              ref={(ref) => {
                if (ref && localVideoTrack &&
                    (lastPlayedVideoRef.current.track !== localVideoTrack || lastPlayedVideoRef.current.element !== ref)) {
                  lastPlayedVideoRef.current = { track: localVideoTrack, element: ref };
                  try {
                    localVideoTrack.play(ref);
                    const videoElement = ref.querySelector('video');
                    if (videoElement) {
                      videoElement.style.width = '100%';
                      videoElement.style.height = '100%';
                      videoElement.style.objectFit = 'contain';
                    }
                  } catch (error) {
                    console.error("[renderPinnedVideoArea] Error playing video track:", error);
                  }
                }
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--background-color-rgba, rgba(0,0,0,0.8))",
                color: "#fff"
              }}
            >
              <Typography variant="h5" sx={{ mb: 2 }}>
                {isOwner ? "📹 Starting Camera..." : "Waiting for host video..."}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Local video track: {localVideoTrack ? 'Available' : 'Not available'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Video enabled: {video ? 'Yes' : 'No'}
              </Typography>
              {isOwner && !localVideoTrack && (
                <Typography variant="body2" sx={{ mt: 2, color: "#ff9800" }}>
                  Please allow camera access if prompted
                </Typography>
              )}
            </Box>
          )}

          {/* Tip animations overlay */}
          <TipAnimationStack tips={tipAnimations} onComplete={removeTipAnimation} />
        </Box>
      );
    }
    const pinnedRemoteObj = remoteUsers.find((r) => r.uid === pinnedUid);
    if (pinnedRemoteObj && pinnedRemoteObj.videoTrack) {
      return (
        <Box
          className="pinned_area"
          sx={{
            position: "relative",
            flex: 1,
            backgroundColor: "var(--background-color, #000)",
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            id={`player-${pinnedUid}`}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "var(--background-color, #000)"
            }}
            ref={(ref) => {
              if (ref && pinnedRemoteObj.videoTrack &&
                  (lastPlayedVideoRef.current.track !== pinnedRemoteObj.videoTrack || lastPlayedVideoRef.current.element !== ref)) {
                lastPlayedVideoRef.current = { track: pinnedRemoteObj.videoTrack, element: ref };
                try {
                  pinnedRemoteObj.videoTrack.play(ref);
                  const videoElement = ref.querySelector('video');
                  if (videoElement) {
                    videoElement.style.width = '100%';
                    videoElement.style.height = '100%';
                    videoElement.style.objectFit = 'contain';
                  }
                } catch (error) {
                  console.error("[renderPinnedVideoArea] Error playing remote video track:", error);
                }
              }
            }}
          />

          {/* Tip animations overlay */}
          <TipAnimationStack tips={tipAnimations} onComplete={removeTipAnimation} />
        </Box>
      );
    }

    return (
      <Box
        className="pinned_area"
        sx={{
          position: "relative",
          flex: 1,
          backgroundColor: "var(--background-color, #000)",
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "#fff", textAlign: "center" }}>No pinned track</Typography>

        {/* Tip animations overlay */}
        <TipAnimationStack tips={tipAnimations} onComplete={removeTipAnimation} />
      </Box>
    );
  };

  const renderCohostContainer = () => {
    const cohosts = [];

    if ((isOwner || isCohost) && pinnedUid !== "local") {
      cohosts.push({ uid: "local", local: true });
    }

    remoteUsers.forEach((r) => {
      if (r.uid !== pinnedUid) {
        if (r.videoTrack) {
          cohosts.push({ uid: r.uid, local: false });
        }
      }
    });

    if (cohosts.length === 0) return null;

    return (
      <Box
        className="cohost_container"
        sx={{
          width: 300,
          height: "100%",
          backgroundColor: "transparent",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px 20px 20px 10px",
          overflowY: "auto",
          flexShrink: 0,
        }}
      >
        {cohosts.map((c) => {
          let displayName = "";
          let trackObj = null;
          if (c.local) {
            displayName = "You (cohost)";
            trackObj = localVideoTrack;
          } else {
            const found = remoteUsers.find((r) => r.uid === c.uid);
            if (found) {
              displayName = fullName(found.user, 12) || `UID ${found.uid}`;
              trackObj = found.videoTrack;
            }
          }
          return (
            <Box
              key={c.uid}
              sx={{
                position: "relative",
                backgroundColor: "#2a2a2a",
                width: "100%",
                height: 400,
                cursor: "pointer",
                borderRadius: "12px",
                overflow: "hidden",
                border: "2px solid rgba(255, 255, 255, 0.1)",
                flexShrink: 0,
              }}
            >
              {trackObj ? (
                <div
                  id={`player-${c.uid}`}
                  style={{ width: "100%", height: "100%" }}
                  ref={(ref) => {
                    if (!ref || playedCohostTilesRef.current.get(ref) === trackObj) return;
                    playedCohostTilesRef.current.set(ref, trackObj);
                    trackObj.play(ref);
                  }}
                />
              ) : (
                <Typography sx={{ color: "#fff", padding: "5px" }}>
                  {displayName}
                </Typography>
              )}

              {pinnedUid !== (c.local ? "local" : c.uid) && (
              <PushPinIcon
                style={{
                  position: "absolute",
                  top: 5,
                  right: 5,
                  width: 24,
                  height: 24,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setPinnedUid(c.local ? "local" : c.uid);
                }}
              />
            )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderAudienceIcons = () => {
    return null;
  };


  if (hasError) {
    return (
      <Box sx={{
        width: "100vw",
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column"
      }}>
        <Typography variant="h5" sx={{ color: "#fff", mb: 2 }}>
          Streaming Error
        </Typography>
        <Typography variant="body1" sx={{ color: "#fff", mb: 3 }}>
          {errorMessage}
        </Typography>
        <IconButton
          onClick={() => window.location.reload()}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" }
          }}
        >
          Reload Page
        </IconButton>
      </Box>
    );
  }

  const effectivePlaylistId = streamData?._id || playlistId;
  // Chat, player and controls need a joined session (RTC token or HLS).
  const hasMedia = Boolean(effectiveTokenData?.token || viewMode === "hls");

  if ((!streamData || !effectivePlaylistId) && !effectiveTokenData?.token) {
    return (
      <Box sx={{
        width: "100vw",
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <Loader wait message="Loading stream data..." />
      </Box>
    );
  }

  return (
    <Box
      id="__streaming_container"
      className={`__streaming_container live-shell${isFullScreen ? " live-shell--fullscreen" : ""}`}
      sx={{
        width: "100vw",
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: "#000",
        overflow: "hidden",
        zIndex: 1000001, // Above global player (1000000) so streaming controls aren't hidden
        display: "flex",
        flexDirection: "column"
      }}
    >
      {connectionState === RECONNECTING_STATE && (
        <Typography
          role="status"
          sx={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            color: "#fff",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            px: 2,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          Reconnecting…
        </Typography>
      )}
      {}
      <Box id="streaming-header-bar" className="live-top">
        {streamData ? (
          <StreamHeader
            isModerator={isModerator}
            moderatorIds={moderatorIds}
            title={streamData.title || streamData.name || 'Live Stream'}
            watchers={liveUser?.length || 0}
            isOwner={isOwner}
            isJoined={isJoined}
            playlistId={effectivePlaylistId}
            streamData={streamData}
            liveUser={liveUser}
            totalUsers={liveUser?.length || 0}
            closeFullscreen={closeFullScreen}
            leaveStreaming={() => setSuccess(true)}
            isFullScreen={isFullScreen}
            openFullScreen={openFullScreen}
            goal={goal}
            title={title}
            goalDescription={goalDescription}
            leaveStreamSentRef={leaveStreamSentRef}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: '#fff' }}>Loading header...</Typography>
          </Box>
        )}
      </Box>

      {tipLoading && (
        <Loader
          wait
          _class="__loader_inside"
          message={message || "Processing..."}
        />
      )}

      <Box className={`live-grid${hasMedia ? "" : " live-grid--no-chat"}`}>
        {hasMedia && (
          <aside className="live-panel live-chat" aria-label={t("stream.liveChat", "Live chat")}>
            <header className="live-panel__head">
              <h2><span className="live-chat__dot" />{t("stream.liveChat", "Live chat")}</h2>
            </header>
            <StreamingChat
              playlistId={effectivePlaylistId}
              isOwner={isOwner}
              closeFullscreen={closeFullScreen}
              leaveStreaming={() => setSuccess(true)}
              streamData={streamData}
              artistId={streamData?.owner}
              isSubscribed
            />
          </aside>
        )}

        <section className="live-stage">
          <Box className="live-video">
            {DAW_IN_LIVE_ENABLED && dawSessionActive ? (
              <Box sx={{ flex: 1, display: "flex", position: "relative", overflow: "hidden" }}>
                <Box sx={{ flex: 1, overflow: "auto" }}>
                  <DAW
                    socket={socket}
                    channelName={effectivePlaylistId}
                    isHost={canModerate}
                    userId={userId}
                    sessionMode
                  />
                </Box>
                {/* Host camera PiP when DAW is active */}
                {isOwner && localVideoTrack && (
                  <Box sx={{
                    position: "absolute", top: 12, right: 12, width: 180, height: 135,
                    borderRadius: 2, overflow: "hidden", border: "2px solid rgba(255,255,255,0.2)",
                    zIndex: 30, boxShadow: "0 4px 20px rgba(0,0,0,0.6)",
                  }}>
                    {renderPinnedVideoArea()}
                  </Box>
                )}
              </Box>
            ) : (
              <>
                {activeStreamVideo ? (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                      backgroundColor: "#000",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", position: "relative" }}>
                      {renderPinnedVideoArea()}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        backgroundColor: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <video
                        key={activeStreamVideo.videoId}
                        src={activeStreamVideo.videoUrl}
                        poster={activeStreamVideo.thumbnailUrl || undefined}
                        autoPlay
                        controls={false}
                        playsInline
                        style={{ width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#000" }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          backgroundColor: "rgba(0,0,0,0.55)",
                          color: "#fff",
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.75,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                          maxWidth: "70%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        📼 {activeStreamVideo.title || "video"}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  renderPinnedVideoArea()
                )}
                {renderCohostContainer()}
                {renderAudienceIcons()}
              </>
            )}

            <div className="live-video__overlay live-video__overlay--left">
              <span className="live-chip live-chip--live"><i />LIVE</span>
              <span className="live-chip"><VisibilityOutlinedIcon />{liveUser?.length || 0}</span>
            </div>
            <div className="live-video__overlay live-video__overlay--right">
              <button
                type="button"
                className="live-chip live-chip--icon"
                aria-label={isFullScreen ? "Exit fullscreen" : "Fullscreen"}
                onClick={isFullScreen ? closeFullScreen : openFullScreen}
              >
                {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </button>
            </div>

            {(goal > 0 || title) && (
              <Box sx={{
                position: "absolute",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 25
              }}>
                <StreamTippers
                  title={title || ""}
                  goal={goal || 0}
                  goalProgress={0}
                  liveUser={liveUser.length}
                  isOwner={isOwner}
                  setStreamTippers={setTippers}
                  channelName={streamData._id}
                  isJoined={isJoined}
                  goalDescription={goalDescription || ""}
                />
              </Box>
            )}
          </Box>

          {hasMedia && (
            <div className="live-strip">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <LivestreamMusicPlayer
                  isHost={isOwner && !isObsAdmin}
                  canControl={isModerator && !isOwner}
                  roomId={streamData?._id || playlistId}
                  streamData={streamData}
                  playHostMusicLocally={playsHostMusic && viewMode === "hls" && !isOwner}
                  hostMusicMuted={hlsMuted}
                  audienceDelaySec={hlsDelaySec}
                  frameClockRef={frameClockRef}
                  hostMusicTimeline={hostMusicTimeline}
                  musicTiming={musicTiming}
                  publishMusicTrack={publishMusicTrack}
                  stopMusicTrack={stopMusicTrack}
                  socket={socket}
                  remoteUsers={remoteUsers}
                  client={client}
                />
              </Box>
              {DAW_IN_LIVE_ENABLED && isOwner && getUserDetail()?.email === 'chris@siki.io' && (
                <IconButton
                  onClick={() => {
                    if (!dawSessionActive && socket) {
                      socket.emit("daw:start-session", {
                        channelName: effectivePlaylistId,
                      });
                      setDawSessionActive(true);
                    } else if (dawSessionActive && socket) {
                      socket.emit("daw:end-session", {
                        channelName: effectivePlaylistId,
                      });
                      stopDAWAudioTrack();
                      setDawSessionActive(false);
                    }
                  }}
                  sx={{
                    color: dawSessionActive ? "#EE772F" : "#999",
                    backgroundColor: dawSessionActive ? "rgba(238,119,47,0.15)" : "transparent",
                    border: dawSessionActive ? "1px solid rgba(238,119,47,0.3)" : "1px solid transparent",
                    borderRadius: 1,
                    padding: "6px 12px",
                    fontSize: 13,
                    "&:hover": { backgroundColor: "rgba(238,119,47,0.1)" },
                  }}
                  title={dawSessionActive ? "Close DAW Session" : "Open DAW Session"}
                >
                  <PianoIcon sx={{ fontSize: 20, mr: 0.5 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "inherit" }}>
                    {dawSessionActive ? "Close DAW" : "DAW"}
                  </Typography>
                </IconButton>
              )}
              <StreamingControls
                role={isOwner ? "host" : isCohost ? "cohost" : "audience"}
                // Streams record automatically; the manual screen-record button is retired.
                showRecordButton={false}
                isOwner={isOwner}
                isModerator={isModerator}
                isObsAdmin={isObsAdmin}
                isJoined={isJoined}
                isVideo={video}
                isMute={isMute}
                video={video}
                mute={handleMuteToggle}
                disableVideo={handleVideoToggle}
                leaveStreaming={() => setSuccess(true)}
                isScreenSharing={isScreenSharing}
                shareScreen={startScreenShare}
                stopShare={stopScreenShare}
                isFullScreen={isFullScreen}
                openFullScreen={openFullScreen}
                closeFullScreen={closeFullScreen}
                musicTrack={musicTrack}
                switchAudio={switchAudio}
                switchVideo={switchVideo}
                publishMusicTrack={publishMusicTrack}
                stopMusicTrack={stopMusicTrack}
                playlistId={playlistId || streamData?._id}
                hostId={streamData?.createdBy || streamData?.createdByDetails?._id || streamData?.owner || "61736afa0a4eff0de720b4aa"}
                userId={userId}
                liveUsers={liveUser}
                onTipHost={handleTipHost}
                onFollowHost={handleFollowHost}
                onSubscribeHost={handleSubscribeHost}
                channelName={effectiveTokenData?.channelName || streamData?._id}
                livestreamId={streamData?._id}
                songData={songData}
                songTimelineRef={songTimelineRef}
                hlsUrl={hlsUrl}
              />
            </div>
          )}
        </section>

        {streamData?._id && (
          <LiveTopSupporters
            channelName={streamData._id}
            userId={userId}
            isOwner={isOwner}
            onTip={isOwner ? null : handleTipHost}
          />
        )}
      </Box>

      {}
      {showActiveListings && (
        <ActiveListings
          streamData={streamData?.createdBy}
          userId={userId}
          role={isOwner || isCohost ? "host" : "audience"}
          isOwner={isOwner}
          onClose={() => setShowActiveListings(false)}
        />
      )}

      {}
      {openUser && (
        <TopListPopup
          followings={[]}
          channelName={streamData._id}
          isOwner={isOwner}
          isUsers
          title={`Live Audience (${liveUser.length})`}
          open={openUser}
          data={liveUser}
          handleClose={() => setOpenUser(false)}
          onTipPoupOpen={(selectedUser) => {
            setUserData(selectedUser);
            setOpenTip(true);
          }}
          onNftPoupOpen={(selectedUser) => {
            setUserData(selectedUser);
            setShowNft(true);
          }}
          onRequestToHost={(id, idx, user) => {
            console.log("[TopListPopup] => requestToHost =>", { id, idx, user });
            socket.emit("requestToCoHost", {
              userId: id,
              role: "host",
              channelName: streamData._id,
              host: userId,
              joined: false,
            });
            setOpenUser(false);
          }}
        />
      )}

      {}
      {showNft && (
        <NftList
          style={{ right: 0, width: 444, zIndex: 100000 }}
          onClose={() => setShowNft(false)}
          fullName={fullName(userData)}
          toUser={userData._id}
        />
      )}

      {}
      {openTip && (
        <TipPopup
          artist={fullName(userData)}
          id={userData._id}
          open={openTip}
          isArtist
          handleClose={handleTip}
          isLivestreamSong={tipMetadata?.tipType === "livestreamSong"}
          tipSplitMessage={tipMetadata?.tipType === "livestreamSong" ? "67.5% to artist, 22.5% to host, 10% platform fee" : null}
        />
      )}

      {}
      {success && (
        <SuccessPopup
          open={success}
          message="Stream ended"
          handleClose={() => {
            console.log("[SuccessPopup] => user pressed close => isOwner?", isOwner);
            if (!isOwner) {
              localCleanupAndExit();
            } else {
              endStream();
            }
          }}
        />
      )}

      {cohostInvite && (
        <Dialog open onClose={() => setCohostInvite(null)}>
          <DialogTitle>Co-host Invitation</DialogTitle>
          <DialogContent>
            <Typography>You have been invited to co-host. Accept?</Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={inviteBusy} onClick={() => {
              setInviteBusy(true);
              const jwt = cookieStorage.getCookie("Authorization");
              const data = cohostInvite; setCohostInvite(null);
              if (socket) socket.emit("cohost-invite:decline", { JWT: jwt, channelName: data.channelName, inviteId: data.inviteId });
            }}>Decline</Button>
            <Button variant="contained" disabled={inviteBusy} onClick={() => {
              setInviteBusy(true);
              const jwt = cookieStorage.getCookie("Authorization");
              const data = cohostInvite; setCohostInvite(null);
              if (socket) socket.emit("cohost-invite:accept", { JWT: jwt, channelName: data.channelName, inviteId: data.inviteId });
            }}>Accept</Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  );
}

export default Streaming;
